import Item from 'src/models/item/Item'
import Model, { encoders, ID, Integer, MODEL_LEVEL } from 'src/models/Model'
import Modifier from 'src/models/modifier-set/Modifier'
import ModifierSet from 'src/models/modifier-set/ModifierSet'

export default class ItemModifierSet extends Model {
  static name = 'ItemModifierSet'

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      item: {
        type: Item,
        output: true,
        reference: true
      },
      modifierSet: {
        type: ModifierSet,
        encode: encoders.hasOne,
        reference: true
      },
      minSelectionCount: {
        type: Integer,
        default: null
      },
      maxSelectionCount: {
        type: Number,
        default: null
      },
      hideFromCustomer: {
        type: Boolean,
        default: false
      },
      defaultModifiers: {
        type: [Modifier],
        encode (data) {
          data.defaultModifierIds = _.map(this.defaultModifiers.data, 'id')
        },
        default: () => ([]),
        reference: true
      },
      defaultModifierIds: {
        type: [ID],
        computed () {
          if (this.defaultModifiers) return _.map(this.defaultModifiers.data, 'id')

          if (!this.data) return []

          return this.data.defaultModifierIds
        },
        encode (data) {
          if (this.defaultModifiers) {
            data.defaultModifierIds = _.map(this.defaultModifiers.data, 'id')
          } else {
            data.defaultModifierIds = this.data.defaultModifierIds
          }
        },
        default: () => ([])
      }
    }
  }

  get isAvailableOnline () {
    return this.modifierSet.isAvailableOnline
  }

  get description () {
    return `${this.item.name} ${this.modifierSet.name}`
  }

  get finalMaxSelectionCount () {
    if (!this.modifierSet.multiselect) {
      return 1
    }

    if (this.maxSelectionCount) {
      return _.min([this.maxSelectionCount, this.modifierSet.modifiers.length])
    }

    return this.modifierSet.modifiers.length
  }

  isDefaultModifier (modifier) {
    return _.includes(this.defaultModifierIds, modifier.id)
  }

  toggleDefaultModifier (modifier) {
    const remove = this.isDefaultModifier(modifier)
    if (remove) {
      this.defaultModifierIds = _.without(this.defaultModifierIds, modifier.id)
    } else {
      if (this.maxDefaultModifiersReached) {
        return
      }
      this.defaultModifierIds.push(modifier.id)
    }
  }

  get maxDefaultModifiers () {
    return _.min([this.modifierSet.multiselect ? null : 1, this.modifierSet.modifiers.data.length])
  }

  get maxDefaultModifiersReached () {
    return this.defaultModifierIds?.length >= _.min([this.maxSelectionCount, this.modifierSet.multiselect ? null : 1, Number.MAX_VALUE])
  }

  get defaultSelectionCountError () {
    if (this.defaultModifierIds?.length > _.min([this.maxSelectionCount, this.modifierSet.multiselect ? null : 1, Number.MAX_VALUE])) {
      return 'Max selection count exceeded'
    } else {
      return false
    }
  }

  get choiceCountDescription () {
    if (!this.modifierSet.multiselect) {
      return 'Choose 1'
    } else if (!this.minSelectionCount && !this.maxSelectionCount) {
      return 'Choose up to ' + this.numberFormat(this.modifierSet.modifiers.data.length)
    } else if (!this.minSelectionCount && this.finalMaxSelectionCount) {
      return 'Choose up to ' + this.numberFormat(this.finalMaxSelectionCount)
    } else if (this.minSelectionCount && !this.finalMaxSelectionCount) {
      return 'Choose ' + this.numberFormat(this.minSelectionCount) + ' to ' + this.modifierSet.modifiers.data.length
    } else if (this.minSelectionCount && this.finalMaxSelectionCount) {
      return 'Choose ' + this.numberFormat(this.minSelectionCount) + ' to ' + this.numberFormat(this.finalMaxSelectionCount)
    } else {
      throw new Error('Unhandle modifier set configuration')
    }
  }

  numberFormat (number) {
    number = +number
    if (!number) {
      return ''
    } else {
      return number.toString()
    }
  }
}
